import { useRef, useState } from 'react'
import { formatDistanceToNow } from 'date-fns'
import { ChevronDown, ChevronUp, RotateCcw } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { MarkdownEditorContainer } from '@/components/MarkdownEditorContainer'
import { MilkdownMarkdownEditor } from '@/components/MilkdownMarkdownEditor'
import { useEditorDraft } from '@/hooks/useEditorDraft'
import { useAgentPrompt, type AgentName } from '@/hooks/useAgentPrompt'

const AGENT_LABELS: Record<AgentName, string> = {
  david: 'David',
  hanna: 'Hanna',
}

function since(timestamp: string) {
  return formatDistanceToNow(new Date(timestamp), { addSuffix: true })
}

/**
 * Reads and edits the system prompt an agent runs on.
 *
 * The prompt is global, so this is scoped to careforce staff rather than to
 * the organization selected above. Saving makes the text live for every
 * organization and appends it to the history, which is where an earlier
 * version is recovered from.
 */
export function AgentPromptSection({ agent }: { agent: AgentName }) {
  const [expanded, setExpanded] = useState(false)
  const [note, setNote] = useState('')
  const { live, versions, save, isLoading, loadError, fetchVersion } =
    useAgentPrompt(agent, expanded)

  // A browser drops a textarea's trailing newline, so comparing against the
  // stored text would call an untouched editor dirty. The server puts the
  // newline back when saving.
  const baseline = (live?.content ?? '').replace(/\n+$/, '')
  const editor = useEditorDraft(baseline, {
    onSave: async (next) => {
      await save(next, note, parentRef.current)
      setNote('')
    },
    successMessage: 'System prompt saved — live for every organization',
  })

  // The version this draft was derived from. Frozen while there are unsaved
  // edits: a refetch can advance `live` past what the author is looking at, and
  // saving against that newer parent would slip by the server's conflict check
  // and replace a version they never saw.
  const parentRef = useRef<string | null>(null)
  if (!editor.dirty) {
    parentRef.current = live?.id ?? null
  }

  const canSave = editor.dirty && note.trim().length > 0

  return (
    <div className="space-y-4 border border-[#e0e7fe] rounded-lg p-4 bg-gradient-to-br from-white to-[#fafbff]">
      <div
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        className="flex items-center justify-between cursor-pointer"
        onClick={() => setExpanded((previous) => !previous)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            setExpanded((previous) => !previous)
          }
        }}>
        <div className="flex items-center gap-3">
          <span className="text-xl">📜</span>
          <div>
            <span className="font-semibold text-base text-gray-800 block">
              {AGENT_LABELS[agent]}&apos;s system prompt
            </span>
            <span className="text-xs text-muted-foreground">
              One prompt shared by every organization — saving makes it live
              everywhere
            </span>
          </div>
          {editor.dirty && (
            <Badge
              variant="outline"
              className="text-amber-700 border-amber-300 shrink-0">
              Unsaved
            </Badge>
          )}
        </div>
        {expanded ? (
          <ChevronUp className="h-4 w-4" />
        ) : (
          <ChevronDown className="h-4 w-4" />
        )}
      </div>

      {expanded && loadError && (
        <div className="space-y-2 pt-4 border-t border-[#e0e7fe]">
          {/* An empty editor here would read as "no prompt saved yet", and
              saving over that would replace the live text with whatever the
              box happened to hold. */}
          <p className="m-0 text-sm text-destructive">
            Could not load the prompt: {loadError.message}
          </p>
          <p className="m-0 text-xs text-muted-foreground">
            David is still running on the last saved version.
          </p>
        </div>
      )}

      {expanded && isLoading && (
        <div className="pt-4 border-t border-[#e0e7fe]">
          <p className="m-0 text-sm text-muted-foreground">Loading…</p>
        </div>
      )}

      {expanded && !loadError && !isLoading && (
        <div className="space-y-3 pt-4 border-t border-[#e0e7fe]">
          {/* The editor scrolls itself rather than the config panel. Crepe's
              toolbar sticks at top:0 of its nearest scroll container, and
              sticky resolves against that container's content box — so with
              the panel scrolling, the panel's own padding pinned the bar that
              far down and a band of prompt showed above it. */}
          <MarkdownEditorContainer>
            {/* The scroll sits on this inner wrapper, not on the
                container: the container is deliberately non-clipping so
                Crepe's heading dropdown can open past the editor's edge,
                and this wrapper is unpositioned so it does not become the
                dropdown's containing block. */}
            <div className="max-h-[32rem] overflow-y-auto">
              {/* Keyed on the draft's editor key so cancelling and restoring
                remount it with the text they put back. */}
              <MilkdownMarkdownEditor
                key={editor.editorKey}
                markdown={editor.draft}
                mode="edit"
                onChange={editor.onChange}
              />
            </div>
          </MarkdownEditorContainer>

          <div className="flex items-center justify-between gap-3">
            <span className="text-xs text-muted-foreground">
              {editor.draft.length.toLocaleString()} characters
              {live && ` · saved ${since(live.created_at)}`}
            </span>
          </div>

          <div className="flex items-center gap-2 rounded-lg border border-[#e0e7fe] bg-white p-3">
            <Input
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder="What is this change for? (shown in the history)"
              aria-label="Reason for this change"
              className="h-8 flex-1 text-sm"
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                editor.reset()
                setNote('')
              }}
              disabled={!editor.dirty || editor.isSaving}>
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={editor.save}
              disabled={!canSave || editor.isSaving}>
              {editor.isSaving ? 'Saving…' : 'Save'}
            </Button>
          </div>

          {editor.dirty && !note.trim() && (
            <p className="m-0 text-xs text-muted-foreground">
              Say what this change is for to enable Save.
            </p>
          )}

          <div className="space-y-2 pt-2">
            <span className="text-sm font-semibold text-gray-800">History</span>
            {/* Five rows tall, scrolling beyond that. The list is the only way
                back to an earlier prompt, so it is capped in height rather
                than in length — every save stays reachable, and so do the
                sections below it. */}
            <div className="max-h-[18rem] space-y-1 overflow-y-auto pr-1">
              {versions.map((version, index) => (
                <div
                  key={version.id}
                  className="flex items-center justify-between gap-3 rounded-md border border-[#e0e7fe] bg-white px-3 py-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-sm text-gray-800">
                        {version.note || 'No reason given'}
                      </span>
                      {index === 0 && (
                        <Badge
                          variant="outline"
                          className="text-emerald-700 border-emerald-300">
                          Live
                        </Badge>
                      )}
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {since(version.created_at)} · {version.created_by}
                    </span>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="shrink-0"
                    disabled={index === 0}
                    onClick={async () => {
                      // History rows carry no text, so fetch this one's body.
                      const full = await fetchVersion(version.id)
                      // `replace`, not `onChange`: the outgoing editor
                      // flushes what it holds as it is destroyed, and only the
                      // guarded swap stops that landing on top of the restore.
                      // Trailing newline stripped to match the baseline, so
                      // restoring what is already live leaves nothing to save.
                      editor.replace(full.content.replace(/\n+$/, ''))
                      // Restoring is still a save, and a save needs a reason.
                      // Writing one for them keeps the button from sitting
                      // there disabled with nothing saying why.
                      // An absolute date: this note is written into the row
                      // and read months later, beside a subtitle showing its
                      // own age, so a relative one would describe two
                      // different moments at once.
                      setNote(
                        `Restore the version from ${new Date(
                          version.created_at
                        ).toLocaleDateString()}`
                      )
                    }}>
                    <RotateCcw className="h-3.5 w-3.5 mr-1" />
                    Restore
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
